import type { ErrorEvent as SentryErrorEvent, Breadcrumb } from '@sentry/react';
import {
    scrubEvent,
    scrubObject,
    scrubString,
    scrubUrl,
    type ScrubbableEvent,
} from '@/_vendored-shared/phiScrub';

/* Sentry-Fehler-Monitoring für care-web, DSGVO-Linie (Founder + DSB, 2026-06-03):
   - NUR im authentifizierten Pflege-Tool aktiv (berechtigtes Interesse Art. 6(1)(f)).
     `initMonitoring` wird ausschließlich aus dem Auth-gebundenen Controller gerufen.
   - Die öffentlichen Portale (Angehörige/Aufnahme = Betroffene) werden NIE getrackt —
     doppelt abgesichert: (1) das SDK wird nur bei Auth dynamisch geladen, (2) `beforeSend`
     verwirft zur Laufzeit jedes Event auf einer öffentlichen Route (SPA-sicher).
   - Kein PII (sendDefaultPii:false), errors-only (tracesSampleRate:0), kein Replay.
   - DSN aus VITE_SENTRY_DSN (Env, nicht hardcoded). PHI-Scrub via geteiltem phiScrub. */

const DSN = import.meta.env.VITE_SENTRY_DSN;

// Öffentliche, unauthentifizierte Routen. Präzise Prefixe — `^/admission/` trifft
// NICHT die authentifizierte Staff-Route `/admissions`.
const PUBLIC_PATHS: readonly RegExp[] = [
    /^\/login/,
    /^\/admission\//,
    /^\/aufnahme(\/|$)/,
    /^\/family\/portal/,
];

function isPublicPath(pathname: string): boolean {
    return PUBLIC_PATHS.some((re) => re.test(pathname));
}

let started = false;

export interface MonitoringUser {
    id: string;
    role?: string;
}

/**
 * Startet Sentry — NUR im authentifizierten Bereich aufrufen. Idempotent. No-op
 * ohne DSN (z. B. solange AVV/DSB-Freigabe aussteht → DSN-Env leer lassen).
 */
export async function initMonitoring(user?: MonitoringUser): Promise<void> {
    if (started || typeof window === 'undefined' || !DSN) return;
    started = true;
    const Sentry = await import('@sentry/react');
    Sentry.init({
        dsn: DSN,
        environment: import.meta.env.MODE,
        sendDefaultPii: false,
        tracesSampleRate: 0,
        replaysSessionSampleRate: 0,
        replaysOnErrorSampleRate: 0,
        beforeSend: (event: SentryErrorEvent): SentryErrorEvent | null => {
            // Betroffenen-Portale nie tracken — auch nicht nach SPA-Navigation aus
            // einer bereits authentifizierten Session.
            if (isPublicPath(window.location.pathname)) return null;
            return scrubEvent(event as unknown as ScrubbableEvent) as unknown as SentryErrorEvent;
        },
        beforeBreadcrumb: (crumb: Breadcrumb): Breadcrumb | null => {
            if (crumb.data) {
                if (typeof crumb.data.url === 'string') crumb.data.url = scrubUrl(crumb.data.url);
                crumb.data = scrubObject(crumb.data) as Record<string, unknown>;
            }
            if (typeof crumb.message === 'string') crumb.message = scrubString(crumb.message);
            return crumb;
        },
    });
    if (user) {
        Sentry.setUser({ id: user.id }); // nur pseudonyme ID, keine E-Mail/Name
        if (user.role) Sentry.setTag('user_role', user.role);
    }
}

/** Stoppt Sentry beim Logout — schließt den Client und leert die Queue. */
export async function stopMonitoring(): Promise<void> {
    if (!started) return;
    started = false;
    const Sentry = await import('@sentry/react');
    Sentry.setUser(null);
    await Sentry.getClient()?.close();
}
